import { useState } from 'react';
import { Compass, FolderOpen, FileText, DollarSign, Truck, ShieldCheck } from 'lucide-react';

const CATEGORIES = [
  {
    title: 'Getting Started',
    icon: Compass,
    faqs: [
      {
        q: 'What is the Client Portal?',
        a: 'A secure extension of your project where you can see your items, proposals, and invoices, track order status, approve proposals, and pay invoices online — all in one place.',
      },
      {
        q: 'How do I get access?',
        a: 'Your designer sends you an email invite. Follow the link, choose a password (at least 8 characters), and you’re signed in automatically. After that, sign in anytime from the client login page with your email and password.',
      },
      {
        q: 'I forgot my password. How do I reset it?',
        a: 'There’s no self-service password reset yet. Contact your designer and ask them to resend your invite — that link lets you set a new password.',
      },
      {
        q: 'I don’t see one of my projects.',
        a: 'Only projects your designer has explicitly shared with your account appear on your Dashboard. If something’s missing, ask your designer to add it.',
      },
    ],
  },
  {
    title: 'Projects & Items',
    icon: FolderOpen,
    faqs: [
      {
        q: 'What am I looking at on the Items tab?',
        a: 'Every product in the project, grouped by room, with quantity, item price, sales tax, shipping, and total — plus its invoice status if it’s already been billed.',
      },
      {
        q: 'Can I filter by room?',
        a: 'Yes — a "Filter by" dropdown appears above the Items and Item Status tabs whenever a project has more than one room.',
      },
      {
        q: 'What’s the difference between "Invoiced" and "Not Invoiced" badges?',
        a: '"Not Invoiced" means the item hasn’t been billed yet. "Invoiced" means it’s on an invoice; a checkmark next to it means that invoice is fully paid, otherwise a balance is still due — use the "Pay Invoice" button on the item to settle it.',
      },
    ],
  },
  {
    title: 'Proposals',
    icon: FileText,
    faqs: [
      {
        q: 'What can I do from the Proposals tab?',
        a: 'Review every proposal for the project, see totals for Approved, Proposed, and Balance Due, and filter by status (Ready For Approval, Amount Due, Declined, Invoiced).',
      },
      {
        q: 'How do I approve a proposal?',
        a: 'Open it, review the line items and totals, click "Approve Proposal," type your full name as your signature, and confirm. Once approved, it’s locked in and shows your name and the approval date.',
      },
      {
        q: 'Can I ask for changes instead of approving?',
        a: 'Yes — click "Request Changes," describe what you’d like updated, and your designer is notified directly. The proposal is marked "Changes Requested" until they follow up.',
      },
      {
        q: 'What if I want to turn down a proposal?',
        a: 'Click "Decline." Your designer is notified. Both approving and declining are final — the action buttons disappear once a proposal is settled.',
      },
      {
        q: 'Why don’t I have an "Approve" button?',
        a: 'Your designer can restrict approval rights on a client account while still letting you view proposals or request changes. Ask your designer if you need approval access.',
      },
    ],
  },
  {
    title: 'Invoices & Payments',
    icon: DollarSign,
    faqs: [
      {
        q: 'What’s the difference between the invoices I see?',
        a: '"Merchandise" invoices bill for items and products ordered on the project; "Time Billing" invoices bill for design hours. Use the filter pills at the top of the Invoices tab to switch between them.',
      },
      {
        q: 'How do I pay an invoice?',
        a: 'Open the invoice and click "Pay Now" — you can pay by card or ACH bank transfer, for any amount from $1 up to the remaining balance. The balance and payment history update as soon as it’s processed.',
      },
      {
        q: 'Can I make a partial payment?',
        a: 'Yes. The payment window lets you enter any amount up to the current balance due — you’re not required to pay the full amount at once.',
      },
      {
        q: 'Where can I see what I’ve already paid?',
        a: 'Scroll to "Payment History" at the bottom of any invoice for a list of past payments with dates and amounts.',
      },
      {
        q: 'Why don’t I see an Invoices tab, or a specific invoice?',
        a: 'Your designer controls invoice visibility for your account. If you expect to see one and don’t, reach out to them directly.',
      },
    ],
  },
  {
    title: 'Item Status & Ordering',
    icon: Truck,
    faqs: [
      {
        q: 'What do the status dots under an item mean?',
        a: 'Each ordered item moves through a set path: Ordered → Back Ordered → Workroom → En Route → Delivered → Installed. The dots fill in as it progresses, and the current stage is highlighted.',
      },
      {
        q: 'Where do I check on an order?',
        a: 'The "Item Status" tab, grouped by room, shows quantity, current status, order date, and a link to the invoice once it’s billed.',
      },
    ],
  },
  {
    title: 'Account & Security',
    icon: ShieldCheck,
    faqs: [
      {
        q: 'Is it safe to enter my card or bank details?',
        a: 'Yes — payments are processed securely through Stripe. InteriorFolio never stores your card or bank account numbers.',
      },
      {
        q: 'How do I sign out?',
        a: 'Click the circle icon in the top-right corner of the portal and select "Sign out."',
      },
      {
        q: 'Can other clients see my project?',
        a: 'No. Only your designer and anyone they’ve explicitly given access to can see your projects — other clients on the platform can’t see your data.',
      },
      {
        q: 'I have a question this page doesn’t answer.',
        a: 'Use "Request Changes" on any proposal or invoice to message your designer directly, or use the contact details in the sidebar if your designer has added their company info.',
      },
    ],
  },
];

export default function ClientFaq() {
  const [openKey, setOpenKey] = useState(null);

  return (
    <div className="cp-animate">
      <h1 className="cp-page-title">Frequently Asked Questions</h1>
      <p className="cp-page-subtitle">
        Answers to common questions about using your Client Portal.
      </p>

      {CATEGORIES.map((category, ci) => {
        const Icon = category.icon;
        return (
          <div key={category.title} className="cp-card cp-faq-category">
            <h2 className="cp-section-title">
              <Icon size={16} className="cp-section-icon" />
              {category.title}
            </h2>
            <ul className="cp-faq-list">
              {category.faqs.map((faq, qi) => {
                const key = `${ci}-${qi}`;
                const isOpen = openKey === key;
                return (
                  <li key={key} className="cp-faq-item">
                    <button
                      className="cp-faq-question"
                      onClick={() => setOpenKey(isOpen ? null : key)}
                      aria-expanded={isOpen}
                    >
                      <span>{faq.q}</span>
                      <span className="cp-faq-icon">{isOpen ? '−' : '+'}</span>
                    </button>
                    {isOpen && (
                      <div className="cp-faq-answer-body">
                        <p>{faq.a}</p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      <div className="cp-card cp-faq-contact">
        <p style={{ fontWeight: 600, color: '#111827' }}>Still have questions?</p>
        <p>Use "Request Changes" on any proposal or invoice, or reach out to your designer directly.</p>
      </div>
    </div>
  );
}
